import { useEffect, useState } from "react";
import { useAuth } from "@/providers/auth-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { getPlayerProfile } from "@/services/player/get-player-profile";
import { PlayerProfileOutput } from "types/web-app";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { CountryFlag } from "@/components/ui/country-flag/country-flag";
import { PokemonTeamDisplay } from "@/components/pokemon/pokemon-team-display";
import { ProfileTeamDisplay } from "@/components/profile/profile-team-display";
import { RouteNames } from "@/types/route-names";
import { Button } from "@/components/ui/buttons/button";
import { SegmentedProgressBar } from "@/components/ui/progress/segmented-progress-bar";
import { CaretIcon } from "@/icons/caret-icon";
import { PokemonCampaignTeamDisplay } from "@/components/pokemon/pokemon-campaign-team-display";
import { FACTION_NAME } from "game/data/factions";

export const RaidCampaignSelectView = () => {
  const { getAccessToken } = useAuth();
  const { viewedPlayerId, setCurrentRoute } = useSPAContext();
  const [profile, setProfile] = useState<PlayerProfileOutput | null>(null);
  const [loading, setLoading] = useState(true);

  const [selectedCampaign, setSelectedCampaign] = useState<
    FACTION_NAME | undefined
  >(undefined);

  useEffect(() => {
    const fetchProfile = async () => {
      const token = getAccessToken();
      if (!token) return;

      try {
        const res = await getPlayerProfile(token, viewedPlayerId);
        if (res.ok && res.data) {
          setProfile(res.data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [getAccessToken, viewedPlayerId]);

  if (loading)
    return (
      <div className="text-center p-4 text-pk-gray-4">Loading profile...</div>
    );
  if (!profile)
    return (
      <div className="text-center p-4 text-pk-gray-4">Profile not found</div>
    );

  return (
    <div className="flex flex-col gap-4 pb-20 p-4 w-full max-w-2xl mx-auto bg-pk-cream-2 h-full ">
      {/* Header Card */}
      <div className="p-1 flex items-center justify-between ">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-pk-gray-2">
              {profile.username}
            </h1>
            <div className="flex items-center gap-2">
              <CountryFlag countryCode={profile.country} size="sm" />
              <span className="text-pk-gray-2 font-bold text-sm">
                {profile.country || "Unknown"}
              </span>
            </div>
          </div>
        </div>

        {profile.is_own_profile && (
          <Button
            variant="beige"
            onClick={() => setCurrentRoute(RouteNames.PROFILE_SETTINGS)}
            className=""
          >
            Swap Team
          </Button>
        )}
      </div>

      {/* Main Team */}
      {profile.main_team.length > 0 && (
        <div className="flex flex-col gap-2 transition-all duration-300">
          <div className="bg-black/20 backdrop-blur-md rounded-xl border border-white/10 overflow-visible">
            <PokemonCampaignTeamDisplay
              pokemons={profile.main_team}
              campaignFaction={selectedCampaign}
            />
          </div>
        </div>
      )}

      {!selectedCampaign && (
        <SelectCampaign setSelectedCampaign={setSelectedCampaign} />
      )}
      {selectedCampaign && (
        <SelectStage
          selectedCampaign={selectedCampaign}
          setSelectedCampaign={setSelectedCampaign}
        />
      )}
    </div>
  );
};

export const SelectCampaign = ({
  setSelectedCampaign,
}: {
  setSelectedCampaign: (campaign: FACTION_NAME) => void;
}) => {
  const handleSelectCampaign = (campaign: FACTION_NAME) => {
    setSelectedCampaign(campaign);
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="w-full text-xl text-pk-beige-2  font-bold ml-1">
        SELECT CAMPAIGN
      </div>

      <div className="flex flex-col w-full gap-4 overflow-x-hidden overflow-y-auto">
        <div
          className="rounded-lg bg-pk-green-4 p-4 border-4 border-pk-gray-2"
          onClick={() => handleSelectCampaign(FACTION_NAME.VERDANT_UNION)}
        >
          <span className="text-pk-gray-5 text-2xl tracking-tighter font-semibold">
            VERDANT UNION
          </span>

          <div className="flex flex-col mt-4">
            <span className="text-pk-gray-5 text-sm tracking-wider font-semibold">
              Progress
            </span>
            <SegmentedProgressBar
              ratio={0.25}
              variant="green"
              containerVariant="green"
              className="w-full"
              maxSquares={50}
              segmented={false}
            />
          </div>
        </div>

        <div
          className="rounded-lg bg-pk-red-4 p-4 border-4 border-pk-gray-2"
          onClick={() => handleSelectCampaign(FACTION_NAME.CINDERFIELDS_FIST)}
        >
          <span className="text-pk-gray-5 text-2xl tracking-tighter font-semibold">
            CINDERFIELDS FIST
          </span>

          <div className="flex flex-col mt-4">
            <span className="text-pk-gray-5 text-sm tracking-wider font-semibold">
              Progress
            </span>
            <SegmentedProgressBar
              ratio={0.25}
              variant="red"
              containerVariant="green"
              className="w-full"
              maxSquares={50}
              segmented={false}
            />
          </div>
        </div>

        <div
          className="rounded-lg bg-pk-blue-4 p-4 border-4 border-pk-gray-2"
          onClick={() => handleSelectCampaign(FACTION_NAME.TEMPEST_TRIBE)}
        >
          <span className="text-pk-gray-5 text-2xl tracking-tighter font-semibold">
            TEMPEST TRIBE
          </span>

          <div className="flex flex-col mt-4">
            <span className="text-pk-gray-5 text-sm tracking-wider font-semibold">
              Progress
            </span>
            <SegmentedProgressBar
              ratio={0.25}
              variant="blue"
              containerVariant="green"
              className="w-full"
              maxSquares={50}
              segmented={false}
            />
          </div>
        </div>

        <div className="rounded-lg bg-pk-night-2 p-4 border-4 border-pk-gray-2">
          <span className="text-pk-gray-5 text-2xl tracking-tighter font-semibold">
            DARK LEGION
          </span>

          <div className="flex flex-col mt-4">
            <span className="text-pk-gray-5 text-sm tracking-wider font-semibold">
              Progress
            </span>
            <SegmentedProgressBar
              ratio={0.25}
              variant="white"
              containerVariant="green"
              className="w-full"
              maxSquares={50}
              segmented={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const SelectStage = ({
  selectedCampaign,
  setSelectedCampaign,
}: {
  selectedCampaign: FACTION_NAME | undefined;
  setSelectedCampaign: (campaign: FACTION_NAME | undefined) => void;
}) => {
  const handleSelectStage = (campaign: FACTION_NAME) => {
    setSelectedCampaign(campaign);
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={() => setSelectedCampaign(undefined)}
          className="bg-pk-gray-2/10 rounded-md"
        >
          <CaretIcon
            direction="left"
            width={32}
            height={32}
            className=" stroke-pk-gray-2 fill-pk-gray-2"
          />
        </button>

        <div className=" text-xl text-pk-gray-3 uppercase font-bold ml-1">
          {selectedCampaign}
        </div>
      </div>

      <div className="flex flex-col w-full gap-4 overflow-x-hidden overflow-y-auto">
        <div className="rounded-lg bg-pk-green-4 p-4 border-4 border-pk-gray-2 flex flex-col gap-2">
          <span className="text-pk-gray-5 text-2xl tracking-tighter font-semibold">
            Roots of War
          </span>

          <span className="text-sm text-pk-gray-2 leading-4 font-semibold opacity-80">
            While rumours spread of a new threat traveling through the verdant
            lands, Sylverglade receives a massive blow from Cinderfields Fist.
          </span>

          <div className="flex flex-col mt-4">
            <span className="text-pk-gray-5 text-sm tracking-wider font-semibold">
              Progress
            </span>
            <SegmentedProgressBar
              ratio={0.25}
              variant="green"
              containerVariant="green"
              className="w-full"
              maxSquares={50}
              segmented={false}
            />
          </div>
        </div>

        <div className="rounded-lg bg-pk-red-4 p-4 border-4 border-pk-gray-2">
          <span className="text-pk-gray-5 text-2xl tracking-tighter font-semibold">
            CINDERFIELDS FIST
          </span>

          <div className="flex flex-col mt-4">
            <span className="text-pk-gray-5 text-sm tracking-wider font-semibold">
              Progress
            </span>
            <SegmentedProgressBar
              ratio={0.25}
              variant="red"
              containerVariant="green"
              className="w-full"
              maxSquares={50}
              segmented={false}
            />
          </div>
        </div>

        <div className="rounded-lg bg-pk-blue-4 p-4 border-4 border-pk-gray-2">
          <span className="text-pk-gray-5 text-2xl tracking-tighter font-semibold">
            TEMPEST TRIBE
          </span>

          <div className="flex flex-col mt-4">
            <span className="text-pk-gray-5 text-sm tracking-wider font-semibold">
              Progress
            </span>
            <SegmentedProgressBar
              ratio={0.25}
              variant="blue"
              containerVariant="green"
              className="w-full"
              maxSquares={50}
              segmented={false}
            />
          </div>
        </div>

        <div className="rounded-lg bg-pk-night-2 p-4 border-4 border-pk-gray-2">
          <span className="text-pk-gray-5 text-2xl tracking-tighter font-semibold">
            DARK LEGION
          </span>

          <div className="flex flex-col mt-4">
            <span className="text-pk-gray-5 text-sm tracking-wider font-semibold">
              Progress
            </span>
            <SegmentedProgressBar
              ratio={0.25}
              variant="white"
              containerVariant="green"
              className="w-full"
              maxSquares={50}
              segmented={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
